import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { Nullable } from 'global';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { QuoteCalcViewModel } from 'modules/quote';
import { CreditApplication, SendOcaLinkSendMethods } from '@trnsact/trnsact-shared-types';
import { SendFlyerButton, useSendFlyer } from 'modules/sendCreditApplication';
import { useEquipmentActions } from 'modules/equipment';
import { DealBuilderQuote } from '../../../../api';
import { DealBuilderEquipmentSource, DealBuilderStep } from '../../../../types';
import { useGoToDealBuilderStep } from '../../../../hooks';
import {
  DEAL_BUILDER_EQUIPMENT_ACTION_ADD,
  DEAL_BUILDER_EQUIPMENT_ACTION_EDIT,
  DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION,
  DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ID,
} from '../../../../constants';
import { EquipmentDetailCard } from './EquipmentDetailCard';

interface Props {
  vo: VendorOpportunity;
  equipmentData: Nullable<DealBuilderEquipmentSource[]>;
  refetchEquipment: VoidFunction;
  quote: Nullable<DealBuilderQuote>;
  loading: boolean;
  priceCalc: Nullable<QuoteCalcViewModel>;
  creditApp: CreditApplication | undefined;
}

export const EquipmentTab = ({ vo, equipmentData, refetchEquipment, quote, loading, priceCalc, creditApp }: Props) => {
  const goToStep = useGoToDealBuilderStep();
  const { onDeleteEquipment } = useEquipmentActions();
  const { sendFlyer, isLoading: isSendingFlyer } = useSendFlyer();

  const quoteEquipment = quote?.equipment ?? null;
  const items = equipmentData ?? [];

  const handleAddEquipment = () => {
    goToStep(DealBuilderStep.Equipment, {
      [DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION]: DEAL_BUILDER_EQUIPMENT_ACTION_ADD,
    });
  };

  const handleEditEquipment = (equipmentId: string) => {
    goToStep(DealBuilderStep.Equipment, {
      [DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION]: DEAL_BUILDER_EQUIPMENT_ACTION_EDIT,
      [DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ID]: equipmentId,
    });
  };

  const handleDeleteEquipment = (equipmentId: string) => {
    onDeleteEquipment(
      {
        equipmentId,
        vendorOpportunityId: vo?.vendorOpportunityId ?? '',
        dynamicsVendorOpportunityId: vo?.dynamicsVendorOpportunityId ?? '',
      },
      refetchEquipment
    );
  };

  const handleSendFlyer = async () => {
    if (!creditApp || items.length === 0) return;

    await sendFlyer({
      contactDetails: {
        email: creditApp.email ?? undefined,
        firstName: creditApp.firstName ?? '',
        lastName: creditApp.lastName ?? '',
        mobilePhone: creditApp.phone ?? undefined,
        businessName: creditApp.businessName ?? undefined,
      },
      sendMethod: SendOcaLinkSendMethods.Email,
      flyerMeta: {
        listOfEquipment: items.map(item => item.equipmentId).filter(Boolean),
        finance: {
          rate: 0,
          term: vo?.lengthOfContractInMonths || 0,
          downPayment: vo?.downPayment || 0,
        },
      },
    });
  };

  const canSendFlyer = !!creditApp?.email && items.length > 0;

  return (
    <Stack gap={1.5} pt={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={500}>
          Equipment Details
        </Typography>
        <Stack direction="row" gap={1}>
          <Button variant="outlined" size="small" onClick={handleAddEquipment}>
            Add Equipment
          </Button>
          <SendFlyerButton
            variant="contained"
            onClick={handleSendFlyer}
            isLoading={isSendingFlyer}
            disabled={!canSendFlyer}
          />
        </Stack>
      </Stack>

      {loading ? (
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No equipment added
        </Typography>
      ) : (
        <Stack gap={1.5}>
          {items.map(item => (
            <EquipmentDetailCard
              key={item.equipmentId}
              item={item}
              quoteEquipment={quoteEquipment?.find(qe => qe.equipmentId === item.equipmentId) ?? null}
              onEdit={handleEditEquipment}
              onDelete={handleDeleteEquipment}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
